import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import './RolesRecursosPermisosMatrix.css';

const ID_PERMISO_VER = 1; // "Ver" permission

// Componente de celda memoizado para máximo rendimiento
const MatrixCell = React.memo(({ idRol, idRecurso, hasPermission, onToggle, disabled, readOnly }) => {
    return (
        <td
            className={`grid-cell-click ${hasPermission ? 'is-granted' : 'is-denied'} ${disabled ? 'is-busy' : ''}`}
            onClick={() => !disabled && !readOnly && onToggle(idRol, idRecurso)}
            style={readOnly ? { cursor: 'not-allowed', opacity: 0.5, background: '#e2e8f0', position: 'relative' } : { position: 'relative' }}
            title={readOnly ? "Rol global (Solo Lectura)" : ""}
        >
            <div className="status-box">
                {hasPermission ? '✓' : '×'}
            </div>
        </td>
    );
});

export default function RolesRecursosPermisosMatrix() {
    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);
    const [recursos, setRecursos] = useState([]);
    const [asignaciones, setAsignaciones] = useState([]);
    const [permisos, setPermisos] = useState([]);
    const [selectedPermisoId, setSelectedPermisoId] = useState(ID_PERMISO_VER);
    const [queryRecurso, setQueryRecurso] = useState('');
    const [showGlobals, setShowGlobals] = useState(false);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const userEmpresaId = getSessionEmpresaId();
    const isSuperAdmin = isSessionSuperAdmin();

    const loadData = async () => {
        setLoading(true);
        try {
            const [resRoles, resRecursos, resAsignaciones, resPermisos] = await Promise.all([
                axios.get(ENDPOINTS.roles),
                axios.get(ENDPOINTS.recursos),
                axios.get(ENDPOINTS.rolesRecursosPermisos),
                axios.get(ENDPOINTS.permisos)
            ]);

            setRoles((resRoles.data || []).map(Models.roles));
            setRecursos((resRecursos.data || []).map(Models.catRecursos));
            setAsignaciones((resAsignaciones.data || []).map(Models.rolesRecursosPermisos));
            setPermisos((resPermisos.data || []).map(Models.catPermisos));
        } catch (error) {
            console.error('Error al cargar datos de la matriz:', error);
            setAlertConfig({
                open: true,
                message: 'No se pudieron cargar los datos de permisos.',
                title: 'Error de carga',
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Create a fast lookup map for assignments: Map<"idRol-idRecurso", idAsignacion>
    const assignmentsMap = useMemo(() => {
        const map = new Map();
        asignaciones.forEach(a => {
            if (a.idPermiso === selectedPermisoId) {
                map.set(`${a.idRol}-${a.idRecurso}`, a.idRolRecursoPermiso);
            }
        });
        return map;
    }, [asignaciones, selectedPermisoId]);

    const filteredRecursos = useMemo(() => {
        const q = queryRecurso.toLowerCase().trim();
        if (!q) return recursos;
        return recursos.filter(r => r.nombre.toLowerCase().includes(q));
    }, [recursos, queryRecurso]);

    const sortedRoles = useMemo(() => {
        let filteredRoles = [...roles];

        const hasLocalRoles = roles.some(r => r.idEmpresa === userEmpresaId);

        if (!isSuperAdmin && hasLocalRoles && !showGlobals) {
            filteredRoles = filteredRoles.filter(r => r.idEmpresa === userEmpresaId);
        }

        return filteredRoles.sort((a, b) => {
            const aIsOwn = a.idEmpresa === userEmpresaId;
            const bIsOwn = b.idEmpresa === userEmpresaId;
            if (aIsOwn && !bIsOwn) return -1;
            if (!aIsOwn && bIsOwn) return 1;

            const aIsGlobal = a.idEmpresa == null;
            const bIsGlobal = b.idEmpresa == null;
            if (!aIsGlobal && bIsGlobal) return -1;
            if (aIsGlobal && !bIsGlobal) return 1;

            return (a.nombre || '').localeCompare(b.nombre || '');
        });
    }, [roles, userEmpresaId, isSuperAdmin, showGlobals]);

    const handleToggle = useCallback(async (idRol, idRecurso) => {
        // Obtenemos el estado actual de la celda de forma eficiente
        // Nota: Para usar el estado más reciente dentro de un callback memoizado, usamos la forma funcional de setAsignaciones
        setAsignaciones(currentAsignaciones => {
            const key = `${idRol}-${idRecurso}`;
            // Re-calculamos el existingId localmente para el toggle inmediato
            const existing = currentAsignaciones.find(a => a.idRol === idRol && a.idRecurso === idRecurso && a.idPermiso === selectedPermisoId);
            const existingId = existing?.idRolRecursoPermiso;

            const nextAsignaciones = [...currentAsignaciones];

            // --- Optimistic Update ---
            if (existingId) {
                // Remove from state
                const filtered = nextAsignaciones.filter(a => a.idRolRecursoPermiso !== existingId);

                // Ejecutamos la llamada al API en background SIN esperar al setAsignaciones
                axios.delete(ENDPOINTS.rrpById(existingId)).then(() => {
                    // Limpiar caché de permisos para refrescar los catálogos de inmediato
                    if (typeof window !== 'undefined' && window.__clearPermissionsCache) {
                        window.__clearPermissionsCache();
                    }
                }).catch(err => {
                    console.error('Error al borrar:', err);
                    setAsignaciones(currentAsignaciones); // Revert
                    setAlertConfig({ open: true, message: 'Fallo al sincronizar el borrado.', title: 'Error', variant: 'error' });
                });

                return filtered;
            } else {
                // Add temp
                const tempId = -Math.round(Math.random() * 1000000);
                const newAsignacion = {
                    idRolRecursoPermiso: tempId,
                    idRol,
                    idRecurso,
                    idPermiso: selectedPermisoId,
                    fechaAsignacion: new Date().toISOString()
                };

                // API Call
                const payload = { idRol, idRecurso, idPermiso: selectedPermisoId };
                axios.post(ENDPOINTS.rolesRecursosPermisos, payload).then(response => {
                    const createdId = typeof response.data === 'number' ? response.data : (response.data.idRolRecursoPermiso || 0);
                    const realItem = { ...newAsignacion, idRolRecursoPermiso: createdId };

                    setAsignaciones(prev => prev.map(a =>
                        (a.idRol === idRol && a.idRecurso === idRecurso && a.idRolRecursoPermiso < 0)
                            ? realItem : a
                    ));
                    // Limpiar caché de permisos para refrescar los catálogos de inmediato
                    if (typeof window !== 'undefined' && window.__clearPermissionsCache) {
                        window.__clearPermissionsCache();
                    }
                }).catch(err => {
                    console.error('Error al insertar:', err);
                    setAsignaciones(currentAsignaciones); // Revert
                    setAlertConfig({ open: true, message: 'Fallo al sincronizar el guardado.', title: 'Error', variant: 'error' });
                });

                return [...nextAsignaciones, newAsignacion];
            }
        });
    }, [selectedPermisoId]);

    return (
        <div className="panel-sigma-main matrix-panel-adjust">
            <div className="roles-topbar">
                <h3 className="roles-title">Matriz de Permisos ({permisos.find(p => p.idPermiso === selectedPermisoId)?.nombre || 'Cargando...'})</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <select
                        className="roles-search"
                        style={{ width: '200px', cursor: 'pointer' }}
                        value={selectedPermisoId}
                        onChange={(e) => setSelectedPermisoId(Number(e.target.value))}
                    >
                        {permisos.map(p => (
                            <option key={p.idPermiso} value={p.idPermiso}>
                                Permiso: {p.nombre}
                            </option>
                        ))}
                    </select>
                    <input
                        className="roles-search"
                        placeholder="Filtrar por recurso..."
                        value={queryRecurso}
                        onChange={(e) => setQueryRecurso(e.target.value)}
                    />
                    {(!isSuperAdmin && roles.some(r => r.idEmpresa === userEmpresaId)) && (
                        <button
                            type="button"
                            className="btn-action"
                            onClick={() => setShowGlobals(!showGlobals)}
                        >
                            👁️ {showGlobals ? 'Ocultar Globales' : 'Ver Globales'}
                        </button>
                    )}
                </div>
            </div>

            <div className="roles-table-wrap matrix-table-card-override">
                <div className="roles-table-scroller matrix-scroller-override">
                    <table className="roles-table matrix-table-override">
                        <thead>
                            <tr>
                                <th className="sticky-col first-col header-corner">
                                    <div className="header-corner-content">Recurso \ Rol</div>
                                </th>
                                {sortedRoles.map(rol => {
                                    const isRoleReadonly = !isSuperAdmin && rol.idEmpresa !== userEmpresaId;
                                    return (
                                        <th key={rol.idRol} className="rol-header-cell" style={isRoleReadonly ? { background: '#e2e8f0' } : {}}>
                                            <div
                                                className="rol-name-vertical"
                                                title={isRoleReadonly ? `${rol.nombre} (Solo Lectura)` : rol.nombre}
                                                style={isRoleReadonly ? { opacity: 0.5, color: '#475569' } : {}}
                                            >
                                                {rol.nombre}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRecursos.map(recurso => (
                                <tr key={recurso.idRecurso}>
                                    <td className="sticky-col first-col recurso-name-cell">
                                        <div className="recurso-label">{recurso.nombre}</div>
                                    </td>
                                    {sortedRoles.map(rol => {
                                        const isRoleReadonly = !isSuperAdmin && rol.idEmpresa !== userEmpresaId;
                                        return (
                                            <MatrixCell
                                                key={`${rol.idRol}-${recurso.idRecurso}`}
                                                idRol={rol.idRol}
                                                idRecurso={recurso.idRecurso}
                                                hasPermission={assignmentsMap.has(`${rol.idRol}-${recurso.idRecurso}`)}
                                                onToggle={handleToggle}
                                                disabled={loading}
                                                readOnly={isRoleReadonly}
                                            />
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
